"use strict";
/**
 * Aerospace Document JSON Schema
 *
 * Structured document model for DO-178C/DO-254 certification documents.
 * Replaces HTML-based storage with JSON/AST for:
 * - Reliable AI editing via operations
 * - Validation of document structure
 * - Audit trails for compliance
 * - Metadata separation from content
 *
 * This is the shared, authoritative version used by both frontend and backend.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.SECTION_TEMPLATES = exports.PROPOSAL_REQUIRED_CHANGES = exports.AUTO_APPROVED_CHANGES = void 0;
exports.findSection = findSection;
exports.findBlock = findBlock;
exports.findSectionContainingBlock = findSectionContainingBlock;
exports.generateId = generateId;
exports.isHierarchicalSectionId = isHierarchicalSectionId;
exports.getSectionNumberFromId = getSectionNumberFromId;
exports.getDisplaySectionNumber = getDisplaySectionNumber;
exports.getSectionTypeFromId = getSectionTypeFromId;
exports.getParentSectionId = getParentSectionId;
exports.normalizeDocumentSections = normalizeDocumentSections;
exports.validateDocumentSections = validateDocumentSections;
exports.computeSectionNumbering = computeSectionNumbering;
/**
 * Change types that are auto-approved (minor changes)
 */
exports.AUTO_APPROVED_CHANGES = [
    'mood_change',
    'temporary_emotion',
    'minor_dialogue',
    'scene_transition',
];
/**
 * Change types that require proposal (major changes)
 */
exports.PROPOSAL_REQUIRED_CHANGES = [
    'character_death',
    'character_injury',
    'relationship_change',
    'status_change',
    'world_change',
];
exports.SECTION_TEMPLATES = [
    {
        id: 'general',
        label: 'General Section',
        icon: '📄',
        customType: 'general',
        defaultTitle: 'New Section',
        description: 'Standard section with no special formatting',
        defaultBlocks: []
    },
    {
        id: 'safety-analysis',
        label: 'Safety Analysis',
        icon: '⚠️',
        customType: 'safety-analysis',
        defaultTitle: 'Safety Analysis',
        description: 'Failure modes, hazards, severity, and mitigations',
        defaultBlocks: [
            {
                id: 'auto-p-1',
                type: 'paragraph',
                content: {
                    text: 'This section presents the safety analysis for the system, including failure modes, hazards, severity classifications, and mitigations.'
                }
            },
            {
                id: 'auto-table-1',
                type: 'table',
                content: {
                    title: 'Failure Modes and Effects Analysis',
                    columns: [
                        { id: 'col-1', label: 'Failure Mode', width: 25 },
                        { id: 'col-2', label: 'Effect', width: 30 },
                        { id: 'col-3', label: 'Severity', width: 15 },
                        { id: 'col-4', label: 'Mitigation', width: 30 }
                    ],
                    rows: [],
                    style: 'standard'
                }
            }
        ]
    },
    {
        id: 'test-results',
        label: 'Test Results',
        icon: '✅',
        customType: 'test-results',
        defaultTitle: 'Test Results',
        description: 'Test execution results and coverage',
        defaultBlocks: [
            {
                id: 'auto-p-1',
                type: 'paragraph',
                content: {
                    text: 'This section summarizes the test execution results.'
                }
            },
            {
                id: 'auto-table-1',
                type: 'table',
                content: {
                    title: 'Test Summary',
                    columns: [
                        { id: 'col-1', label: 'Test ID', width: 15 },
                        { id: 'col-2', label: 'Requirement', width: 20 },
                        { id: 'col-3', label: 'Description', width: 30 },
                        { id: 'col-4', label: 'Result', width: 15 },
                        { id: 'col-5', label: 'Notes', width: 20 }
                    ],
                    rows: [],
                    style: 'test-matrix'
                }
            }
        ]
    },
    {
        id: 'requirements',
        label: 'Requirements',
        icon: '📋',
        customType: 'requirements',
        defaultTitle: 'Requirements',
        description: 'System or software requirements with traceability',
        defaultBlocks: [
            {
                id: 'auto-p-1',
                type: 'paragraph',
                content: {
                    text: 'This section defines the requirements for the system.'
                }
            }
        ]
    },
    {
        id: 'architecture',
        label: 'Architecture',
        icon: '🏗️',
        customType: 'architecture',
        defaultTitle: 'Architecture',
        description: 'System architecture and design overview',
        defaultBlocks: [
            {
                id: 'auto-p-1',
                type: 'paragraph',
                content: {
                    text: 'This section describes the system architecture.'
                }
            }
        ]
    },
    {
        id: 'traceability',
        label: 'Traceability Matrix',
        icon: '🔗',
        customType: 'traceability',
        defaultTitle: 'Traceability Matrix',
        description: 'Requirements traceability to design and verification',
        defaultBlocks: [
            {
                id: 'auto-p-1',
                type: 'paragraph',
                content: {
                    text: 'This section provides traceability between requirements, design elements, and verification activities.'
                }
            },
            {
                id: 'auto-table-1',
                type: 'table',
                content: {
                    title: 'Traceability Matrix',
                    columns: [
                        { id: 'col-1', label: 'Requirement ID', width: 20 },
                        { id: 'col-2', label: 'Design Element', width: 25 },
                        { id: 'col-3', label: 'Test Case', width: 20 },
                        { id: 'col-4', label: 'Verification Method', width: 20 },
                        { id: 'col-5', label: 'Status', width: 15 }
                    ],
                    rows: [],
                    style: 'traceability'
                }
            }
        ]
    },
    {
        id: 'verification',
        label: 'Verification Procedure',
        icon: '🔍',
        customType: 'verification',
        defaultTitle: 'Verification Procedure',
        description: 'Step-by-step verification procedures',
        defaultBlocks: [
            {
                id: 'auto-p-1',
                type: 'paragraph',
                content: {
                    text: 'This section defines the verification procedures.'
                }
            }
        ]
    },
    {
        id: 'compliance',
        label: 'Compliance Statement',
        icon: '✓',
        customType: 'compliance',
        defaultTitle: 'Compliance Statement',
        description: 'Compliance with standards and regulations',
        defaultBlocks: [
            {
                id: 'auto-p-1',
                type: 'paragraph',
                content: {
                    text: 'This section documents compliance with applicable standards and regulations.'
                }
            }
        ]
    }
];
// ===== Utility Types =====
/**
 * Helper to find a section by ID
 */
function findSection(doc, sectionId) {
    // Search body
    for (const section of doc.body) {
        if (section.id === sectionId)
            return section;
        // Could recursively search children if needed
    }
    // Search back matter
    for (const section of doc.backMatter) {
        if (section.id === sectionId)
            return section;
    }
    return null;
}
/**
 * Helper to find a block by ID
 */
function findBlock(doc, blockId) {
    const sections = [...doc.body, ...doc.backMatter];
    for (const section of sections) {
        const block = section.blocks.find(b => b.id === blockId);
        if (block)
            return block;
    }
    return null;
}
/**
 * Helper to find section containing a block
 */
function findSectionContainingBlock(doc, blockId) {
    const sections = [...doc.body, ...doc.backMatter];
    for (const section of sections) {
        if (section.blocks.some(b => b.id === blockId)) {
            return section;
        }
    }
    return null;
}
/**
 * Generate a unique ID for sections or blocks
 */
function generateId(prefix = 'id') {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}
/**
 * Check if a section ID is hierarchical (sec-1, sec-1-2) vs random (sec-timestamp-random)
 */
function isHierarchicalSectionId(sectionId) {
    if (!sectionId || !sectionId.startsWith('sec-'))
        return false;
    // Hierarchical IDs have short numeric parts: sec-1, sec-1-2, sec-3-2-1
    // Random IDs have long alphanumeric parts: sec-1764019648651-0510nfbqg
    const parts = sectionId.replace('sec-', '').split('-');
    // Check if all parts are short numbers (1-3 digits) or single letters (appendix)
    return parts.every(part => /^[0-9]{1,3}$/.test(part) || /^[a-z]$/i.test(part));
}
/**
 * Derive section number from section ID (for hierarchical IDs only)
 *
 * For hierarchical IDs (sec-1, sec-1-2):
 *   sec-1 → "1"
 *   sec-1-1 → "1.1"
 *   sec-3-2-1 → "3.2.1"
 *   sec-a → "A" (appendix)
 *   sec-a-1 → "A.1"
 *
 * For random IDs (sec-timestamp-random):
 *   Returns null - caller should use section.number field
 */
function getSectionNumberFromId(sectionId) {
    if (!sectionId)
        return null;
    // Only derive from hierarchical IDs
    if (!isHierarchicalSectionId(sectionId)) {
        return null;
    }
    // Handle appendix sections (sec-a, sec-b, etc.)
    const appendixMatch = sectionId.match(/^sec-([a-z])(-.*)?$/i);
    if (appendixMatch) {
        const letter = appendixMatch[1].toUpperCase();
        const rest = appendixMatch[2];
        if (rest) {
            // sec-a-1-2 → A.1.2
            return letter + rest.replace(/-/g, '.').replace(/^\./, '.');
        }
        return letter;
    }
    // Handle regular sections (sec-1, sec-1-1, sec-3-2-1)
    return sectionId.replace('sec-', '').replace(/-/g, '.');
}
/**
 * Get the display number for a section
 * Uses ID-derived number for hierarchical IDs, falls back to section.number for random IDs
 */
function getDisplaySectionNumber(section) {
    const derivedNumber = getSectionNumberFromId(section.id);
    return derivedNumber || section.number || '';
}
/**
 * Derive section type from section ID based on depth
 */
function getSectionTypeFromId(sectionId) {
    if (!sectionId || !sectionId.startsWith('sec-'))
        return 'section';
    // Check for appendix
    if (/^sec-[a-z]/i.test(sectionId))
        return 'appendix';
    // Count depth by number of dashes after 'sec-'
    const parts = sectionId.replace('sec-', '').split('-');
    if (parts.length === 1)
        return 'section'; // sec-1
    if (parts.length === 2)
        return 'subsection'; // sec-1-1
    return 'subsubsection'; // sec-1-1-1 or deeper
}
/**
 * Get parent section ID from a section ID
 *
 * Examples:
 *   sec-1-1 → sec-1
 *   sec-3-2-1 → sec-3-2
 *   sec-1 → null (top-level)
 */
function getParentSectionId(sectionId) {
    if (!sectionId || !sectionId.startsWith('sec-'))
        return null;
    const parts = sectionId.split('-');
    if (parts.length <= 2)
        return null; // sec-1 has no parent
    // Remove last part: sec-1-2-3 → sec-1-2
    return parts.slice(0, -1).join('-');
}
/**
 * Normalize a document's section structure
 * For hierarchical IDs: derives number, type, parent from ID
 * For random IDs: preserves existing number, derives type from parent relationship
 * Call this before saving to ensure consistency
 */
function normalizeDocumentSections(doc) {
    for (const section of doc.body) {
        // Only derive from ID if it's hierarchical
        if (isHierarchicalSectionId(section.id)) {
            // Derive number from ID
            const derivedNumber = getSectionNumberFromId(section.id);
            if (derivedNumber) {
                section.number = derivedNumber;
            }
            // Derive type from ID
            section.type = getSectionTypeFromId(section.id);
            // Derive parent from ID
            const parentId = getParentSectionId(section.id);
            section.parent = parentId || undefined;
        }
        // For random IDs, keep existing number but ensure type is set correctly
        else {
            // Derive type from depth (based on parent chain)
            if (!section.parent) {
                section.type = 'section';
            }
            else {
                const parentSection = doc.body.find(s => s.id === section.parent);
                if (parentSection?.type === 'section') {
                    section.type = 'subsection';
                }
                else if (parentSection?.type === 'subsection') {
                    section.type = 'subsubsection';
                }
            }
        }
        // Ensure children array exists
        if (!section.children) {
            section.children = [];
        }
    }
    // Rebuild children arrays based on parent relationships
    rebuildChildrenArrays(doc.body);
    return doc;
}
/**
 * Rebuild children arrays from parent relationships
 */
function rebuildChildrenArrays(sections) {
    // Clear all children arrays
    for (const section of sections) {
        section.children = [];
    }
    // Rebuild from parent relationships
    for (const section of sections) {
        if (section.parent) {
            const parent = sections.find(s => s.id === section.parent);
            if (parent && !parent.children.includes(section.id)) {
                parent.children.push(section.id);
            }
        }
    }
    // Sort children by their numeric order
    for (const section of sections) {
        section.children.sort((a, b) => {
            const numA = getSectionNumberFromId(a) || a;
            const numB = getSectionNumberFromId(b) || b;
            return numA.localeCompare(numB, undefined, { numeric: true });
        });
    }
}
/**
 * Validate document section structure
 * Returns array of validation errors (empty if valid)
 */
function validateDocumentSections(doc) {
    const errors = [];
    const seenIds = new Set();
    for (const section of doc.body) {
        // Check for duplicate IDs
        if (seenIds.has(section.id)) {
            errors.push(`Duplicate section ID: ${section.id}`);
        }
        seenIds.add(section.id);
        // Check ID format
        if (!section.id.startsWith('sec-')) {
            errors.push(`Invalid section ID format: ${section.id} (must start with 'sec-')`);
        }
        // Only validate number for hierarchical IDs
        if (isHierarchicalSectionId(section.id)) {
            // Check number matches ID
            const expectedNumber = getSectionNumberFromId(section.id);
            if (section.number !== expectedNumber) {
                errors.push(`Section ${section.id} has number "${section.number}" but should be "${expectedNumber}"`);
            }
            // Check parent matches ID structure
            const expectedParent = getParentSectionId(section.id);
            if (section.parent !== expectedParent && section.parent !== undefined) {
                errors.push(`Section ${section.id} has parent "${section.parent}" but should be "${expectedParent || 'null'}"`);
            }
        }
        // Check parent exists
        if (section.parent && !doc.body.some(s => s.id === section.parent)) {
            errors.push(`Section ${section.id} references non-existent parent: ${section.parent}`);
        }
        // Check children exist and have correct parent
        for (const childId of section.children || []) {
            const child = doc.body.find(s => s.id === childId);
            if (!child) {
                errors.push(`Section ${section.id} references non-existent child: ${childId}`);
            }
            else if (child.parent !== section.id) {
                errors.push(`Section ${section.id} lists child ${childId} but child's parent is "${child.parent}"`);
            }
        }
    }
    return errors;
}
/**
 * @deprecated Use getSectionNumberFromId instead - ID is source of truth
 * Compute section numbering from structure (legacy function)
 */
function computeSectionNumbering(sections) {
    // Just normalize based on IDs - this is the new behavior
    for (const section of sections) {
        const derivedNumber = getSectionNumberFromId(section.id);
        if (derivedNumber) {
            section.number = derivedNumber;
        }
        section.type = getSectionTypeFromId(section.id);
        section.parent = getParentSectionId(section.id) || undefined;
    }
    rebuildChildrenArrays(sections);
    return sections;
}
//# sourceMappingURL=document-schema.js.map